import { useEffect, useState, type CSSProperties } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type CustomSkeletonVariant =
  | "single"
  | "multiLine"
  | "titleDescription"
  | "avatar"
  | "card"
  | "multiLineWithAccordion";

interface CustomSkeletonProps {
  variant?: CustomSkeletonVariant;
  width?: string;
  height?: string;
  /** Legacy Chakra radius token; the design-system radius is always used. */
  borderRadius?: string;
  noOfLines?: number;
  className?: string;
}

const CustomSkeleton = ({
  variant = "single",
  width = "100%",
  height = "16px",
  noOfLines = 3,
  className
}: CustomSkeletonProps) => {
  const [visibleLines, setVisibleLines] = useState(
    variant === "single" ? noOfLines : 0
  );

  useEffect(() => {
    if (variant === "single") return;
    setVisibleLines(1); // Start with one line
  }, [variant]);

  // Lines appear one after another; each reveal waits for the previous one.
  useEffect(() => {
    if (variant === "single" || visibleLines >= noOfLines) return undefined;
    if (visibleLines === 0) return undefined;
    const timer = window.setTimeout(
      () => setVisibleLines((prev) => (prev < noOfLines ? prev + 1 : prev)),
      150
    );
    return () => window.clearTimeout(timer);
  }, [variant, visibleLines, noOfLines]);

  const lineStyle: CSSProperties = { height };

  const renderLine = (index: number) => (
    <Skeleton
      key={index}
      className={index === noOfLines - 1 ? "w-4/5" : "w-full"}
      style={lineStyle}
    />
  );

  const lineElements = Array.from({ length: visibleLines }).map((_, index) =>
    renderLine(index)
  );

  const variants: Record<CustomSkeletonVariant, React.JSX.Element> = {
    single: <Skeleton className={className} style={{ width, height }} />,
    multiLine: (
      <div className={cn("flex w-full items-center p-4", className)}>
        <div className="flex w-full flex-col items-start gap-4">
          {lineElements}
        </div>
      </div>
    ),
    multiLineWithAccordion: (
      <div className={cn("flex w-full items-center p-4", className)}>
        <Skeleton className="mr-2 w-6 shrink-0" style={{ minHeight: height }} />
        <div className="flex w-full flex-col items-start gap-2">
          {lineElements}
        </div>
      </div>
    ),
    titleDescription: (
      <div className={cn("flex flex-col items-start gap-3", className)}>
        <Skeleton className="h-4 w-1/2" />
        <div className="flex w-full flex-col items-start gap-2">
          {lineElements}
        </div>
      </div>
    ),
    avatar: (
      <div className={cn("flex items-center gap-4", className)}>
        <Skeleton className="size-24 shrink-0 rounded-full" />
        <div className="flex w-full flex-col items-start gap-2">
          {lineElements}
        </div>
      </div>
    ),
    card: (
      <div
        className={cn(
          "flex w-full flex-col gap-4 rounded-xl p-4 ring-1 ring-line-2",
          className
        )}
      >
        <Skeleton className="h-37.5 w-full" />
        <div className="flex w-full flex-col items-start gap-2">
          {lineElements}
        </div>
      </div>
    )
  };

  return variants[variant];
};

export default CustomSkeleton;
